<template>
    <div class="space-y-6">
        <!-- Page Header -->
        <div class="text-center mb-8">
            <!-- Icon -->
            <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            </div>

            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                Select Company
            </h1>
            <p class="text-secondary-600 dark:text-secondary-400">
                Choose which company you'd like to access
            </p>
        </div>

        <!-- User Info -->
        <div class="mb-8 p-4 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg">
            <div class="flex items-center space-x-3">
                <!-- Avatar -->
                <div class="flex-shrink-0 w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {{ userInitials }}
                </div>
                <div>
                    <p class="text-sm font-medium text-secondary-900 dark:text-white">
                        {{ user?.first_name }} {{ user?.last_name }}
                    </p>
                    <p class="text-xs text-secondary-600 dark:text-secondary-400">
                        {{ user?.email }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Companies Grid -->
        <div v-if="companies && companies.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <div
                v-for="company in companies"
                :key="company.id"
                @click="selectCompany(company.id)"
                :class="[
                    'relative bg-white dark:bg-secondary-800 rounded-lg border-2 p-6 cursor-pointer transition-all duration-200',
                    selectedCompanyId === company.id
                        ? 'border-primary-500 shadow-lg ring-2 ring-primary-200 dark:ring-primary-800'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md',
                    form.processing ? 'opacity-50 cursor-not-allowed' : ''
                ]"
            >
                <!-- Selected Checkmark -->
                <div
                    v-if="selectedCompanyId === company.id"
                    class="absolute top-4 right-4 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center"
                >
                    <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>

                <!-- Company Avatar/Logo -->
                <div class="flex items-start space-x-4 mb-4">
                    <div class="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                        <span class="text-white text-xl font-bold">
                            {{ getCompanyInitials(company.name) }}
                        </span>
                    </div>

                    <div class="flex-1 min-w-0">
                        <h3 class="text-lg font-semibold text-secondary-900 dark:text-white truncate mb-1">
                            {{ company.name }}
                        </h3>

                        <!-- Role Badge -->
                        <p class="text-sm text-secondary-600 dark:text-secondary-400">
                            {{ company.role }}
                        </p>
                    </div>
                </div>

                <!-- Default Badge -->
                <div v-if="company.is_default" class="mb-3">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        Default
                    </span>
                </div>

                <!-- Last Accessed -->
                <div v-if="company.last_accessed_at" class="flex items-center text-xs text-secondary-500 dark:text-secondary-500">
                    <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Last accessed: {{ formatDate(company.last_accessed_at) }}
                </div>
                <div v-else class="text-xs text-secondary-400 dark:text-secondary-600">
                    Never accessed
                </div>

                <!-- Select Button (appears on hover or when selected) -->
                <div
                    :class="[
                        'mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700 transition-opacity',
                        selectedCompanyId === company.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    ]"
                >
                    <button
                        type="button"
                        @click.stop="selectCompany(company.id)"
                        :class="[
                            'w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors',
                            selectedCompanyId === company.id
                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200 dark:bg-secondary-700 dark:text-secondary-300 dark:hover:bg-secondary-600'
                        ]"
                    >
                        {{ selectedCompanyId === company.id ? 'Selected' : 'Select' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-secondary-900 dark:text-white">No companies found</h3>
            <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                You don't have access to any companies yet.
            </p>
        </div>

        <!-- Continue Button (Fixed at bottom) -->
        <div v-if="selectedCompanyId" class="sticky bottom-0 bg-white dark:bg-secondary-800 pt-4 pb-2 -mx-8 px-8 border-t border-secondary-200 dark:border-secondary-700">
            <button
                @click="submitSelection"
                :disabled="form.processing"
                class="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg
                    v-if="form.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="form.processing">Switching to Company...</span>
                <span v-else>Continue to Dashboard</span>
            </button>
        </div>

        <!-- Divider -->
        <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-200 dark:border-secondary-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    or
                </span>
            </div>
        </div>

        <!-- Logout Button -->
        <button
            @click="logout"
            type="button"
            class="block w-full text-center px-4 py-2.5 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200 transition-colors font-medium"
        >
            Logout
        </button>

        <!-- Info Box -->
        <div class="mt-8 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div class="text-sm">
                    <p class="text-info-800 dark:text-info-200 font-medium mb-1">
                        Need access to another company?
                    </p>
                    <p class="text-info-700 dark:text-info-300">
                        You can switch between companies anytime from your dashboard using the company switcher.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { router, useForm, usePage } from '@inertiajs/vue3';

// Props
const props = defineProps({
    companies: {
        type: Array,
        required: true,
    },
});

// Page props
const page = usePage();
const user = computed(() => page.props.auth?.user || null);

// User initials
const userInitials = computed(() => {
    if (!user.value) return '??';
    const first = user.value.first_name?.charAt(0) || '';
    const last = user.value.last_name?.charAt(0) || '';
    return (first + last).toUpperCase() || '??';
});

// Form
const form = useForm({
    company_id: null,
});

// Selected company
const selectedCompanyId = ref(null);

// Select company
const selectCompany = (companyId) => {
    if (form.processing) return;
    selectedCompanyId.value = companyId;
    form.company_id = companyId;
};

// Submit selection
const submitSelection = () => {
    if (!selectedCompanyId.value || form.processing) return;

    form.post('/api/auth/select-company', {
        preserveScroll: true,
        onSuccess: () => {
            router.visit('/dashboard');
        },
        onError: () => {
            // Error handling
            selectedCompanyId.value = null;
            form.company_id = null;
        },
    });
};

// Logout
const logout = () => {
    router.post('/api/auth/logout', {}, {
        onSuccess: () => {
            router.visit('/login');
        },
    });
};

// Get company initials from name
const getCompanyInitials = (name) => {
    if (!name) return '??';

    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }

    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

// Format date
const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    // Just now (less than 1 minute)
    if (diffInSeconds < 60) return 'just now';

    // Minutes ago (less than 1 hour)
    if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    // Hours ago (less than 1 day)
    if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    // Days ago (less than 30 days)
    if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    // Format as date
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity, box-shadow;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
}

/* Hover effect for company cards */
.group:hover .opacity-0 {
    opacity: 1;
}
</style>
